import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import katex from 'katex';
import 'katex/dist/contrib/mhchem.mjs';
// Styles ship separately in `@240tutoring/labs-ui/rich-content.css`.
const TABLE_WRAPPER = 'labs-rich-content-table';
function safeUrl(value) {
    try {
        const url = new URL(value);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
    }
    catch {
        return undefined;
    }
}
/** Port of the study app renderer. Sanitize after image/math conversion, including generated HTML. */
export function convertRichContentToHTML(content) {
    const doc = new DOMParser().parseFromString(content.text || '<p></p>', 'text/html');
    doc.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') ?? '';
        if (src.startsWith('data:,latex:')) {
            const code = doc.createElement('code');
            code.className = 'latex';
            code.textContent = src.slice(12);
            img.replaceWith(code);
        }
        else if (src.startsWith('data:,')) {
            const id = src.slice(6).toLowerCase();
            const file = content.images?.find(f => f.id?.toLowerCase() === id || f.name?.toLowerCase() === id);
            const url = file && safeUrl(file.url);
            if (url) {
                img.src = url;
                img.alt = file.alttext ?? file.name ?? '';
            }
            else
                img.remove();
        }
        else if (!safeUrl(src))
            img.remove();
    });
    doc.querySelectorAll('code.latex, span.latex').forEach(code => {
        const displayMode = code.classList.contains('display') || code.parentElement?.childNodes.length === 1;
        const math = doc.createElement(displayMode ? 'div' : 'span');
        math.innerHTML = katex.renderToString(code.textContent ?? '', { throwOnError: false, displayMode, trust: false, output: 'htmlAndMathml' });
        code.replaceWith(math);
    });
    doc.querySelectorAll('video, audio').forEach(media => {
        const isVideo = media.tagName === 'VIDEO';
        const id = media.getAttribute('src')?.replace(/^data:,/, '');
        const file = (isVideo ? content.videos : content.sounds)?.find(f => f.id === id);
        const text = doc.createElement('p');
        text.textContent = `${isVideo ? 'Video' : 'Audio'}: ${file?.name ?? 'Published media'}`;
        media.replaceWith(text);
    });
    doc.querySelectorAll('a').forEach(a => {
        const href = safeUrl(a.getAttribute('href') ?? '');
        if (href) {
            a.href = href;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
        }
        else
            a.removeAttribute('href');
    });
    doc.querySelectorAll('table').forEach(table => {
        const wrapper = doc.createElement('div');
        wrapper.className = TABLE_WRAPPER;
        table.replaceWith(wrapper);
        wrapper.appendChild(table);
    });
    doc.querySelectorAll('td[data-colwidth], th[data-colwidth]').forEach(cell => {
        const width = Number(cell.dataset.colwidth);
        if (Number.isFinite(width) && width > 0 && width <= 2000) {
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
        }
    });
    // Authored accordion sections become native, keyboard-operable disclosures.
    doc.querySelectorAll('div.accordion').forEach(accordion => {
        const details = doc.createElement('details');
        const summary = doc.createElement('summary');
        summary.textContent = accordion.firstElementChild?.textContent || 'Read more';
        accordion.firstElementChild?.remove();
        details.appendChild(summary);
        while (accordion.firstChild)
            details.appendChild(accordion.firstChild);
        accordion.replaceWith(details);
    });
    return DOMPurify.sanitize(doc.body.innerHTML, { USE_PROFILES: { html: true, mathMl: true }, ADD_ATTR: ['target', 'encoding'], ADD_TAGS: ['annotation', 'semantics'], FORBID_TAGS: ['style', 'iframe', 'form', 'input', 'button'] });
}
export function RichContent({ content }) {
    const ref = useRef(null);
    const html = useMemo(() => convertRichContentToHTML(content), [content]);
    useEffect(() => {
        const wrappers = ref.current?.querySelectorAll(`.${TABLE_WRAPPER}`) ?? [];
        const update = () => wrappers.forEach(el => {
            if (el.scrollWidth > el.clientWidth) {
                el.tabIndex = 0;
                el.setAttribute('role', 'region');
                el.setAttribute('aria-label', 'Scrollable table');
            }
            else {
                el.removeAttribute('tabindex');
                el.removeAttribute('role');
                el.removeAttribute('aria-label');
            }
        });
        update();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        wrappers.forEach(el => observer?.observe(el));
        return () => observer?.disconnect();
    }, [html]);
    return _jsx("div", { ref: ref, className: "labs-rich-content", dangerouslySetInnerHTML: { __html: html } });
}
